import DirectionsCarOutlinedIcon from '@mui/icons-material/DirectionsCarOutlined';
import {
  Box,
  Button,
  Chip,
  colors,
  Divider,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';

import { IcDeleteTrash } from '@/assets/images';
import { useFetchSasToken } from '@/hooks';
import { getActiveLicensePlate } from '@/layouts/TabLayout/MasterVehicles/licensePlate.utils';
import { IMasterVehicle } from '@/models';
import { getAddressCommonString } from '@/services';
import { formatPersonName, getFileUrlWithValidToken } from '@/utils';

import { TabType } from '../tabs/CarStopCfsTab';

type CarDetailsCardProps = {
  data: IMasterVehicle;
  setSelectedTab: (tab: TabType) => void;
  onRemove?: () => void;
};

const DetailsItem = ({ label, value }: { label: string; value?: string }) => (
  <Stack
    display="flex"
    flexDirection="row"
    alignItems="center"
    columnGap={2}
    minHeight={32}
  >
    <Typography fontWeight={600} fontSize={16} width={220} flexShrink={0}>
      {label}
    </Typography>
    <Typography flex={1} color="text.secondary" fontSize={16}>
      {value}
    </Typography>
  </Stack>
);

const RelationsItems = ({ setSelectedTab }: CarDetailsCardProps) => {
  const linked = {
    CFS: null,
    Cases: null,
  };

  return (
    <Stack gap={0.5} flex={1}>
      {/* TODO: linked cases is missing */}
      {(['CFS', 'Cases'] as Array<keyof typeof linked>).map((item) => (
        <Stack
          key={item}
          display="flex"
          flexDirection="row"
          alignItems="center"
          minHeight={32}
        >
          <Typography fontWeight={600} fontSize={16} flex={1}>
            {item} {linked[item] && <Chip color="error" label={linked[item]} />}
          </Typography>
          <Box flex={1} display="flex" alignItems="center" minHeight={32}>
            {linked[item] && (
              <Button
                variant="text"
                onClick={() =>
                  item === 'CFS'
                    ? setSelectedTab('relations-cfs')
                    : setSelectedTab('relations-cases')
                }
              >
                View more
              </Button>
            )}
          </Box>
        </Stack>
      ))}
    </Stack>
  );
};

const CarDetailsCard = ({
  data,
  setSelectedTab,
  onRemove,
}: CarDetailsCardProps) => {
  const sasToken = useFetchSasToken(data.files);
  const activePlate = getActiveLicensePlate(data);

  const personDetailsItems = [
    {
      label: 'Vehicle owner',
      value: data?.owner ? formatPersonName(data?.owner) : '/',
    },
    {
      label: 'Owner address',
      value: getAddressCommonString(data.owner?.address) || '/',
    },
    { label: 'Vehicle color', value: data?.color || '/' },
    { label: 'Vehicle year', value: data?.year || '/' },
    {
      label: 'Registration status',
      value: data?.isVerified ? 'Verified' : 'Not Verified',
    },
    { label: 'VIN', value: data?.vin || '/' },
  ];

  return (
    <Stack
      padding={2}
      border={1}
      borderRadius={1}
      borderColor={colors.grey[300]}
      position="relative"
      minHeight={560}
    >
      <Stack direction="row" alignItems="flex-start" gap={2}>
        <Stack gap={0.75} flex={1} minWidth={0}>
          <Typography
            fontWeight={600}
            fontSize={24}
            sx={{
              lineHeight: 1.15,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {data?.make} {data?.model}
          </Typography>
          <Typography
            fontWeight={400}
            fontSize={18}
            color="text.secondary"
            minHeight={32}
            sx={{
              lineHeight: 1.2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {activePlate?.number || ' '}
          </Typography>
        </Stack>
        <Stack direction="row" alignItems="flex-start" gap={1} flexShrink={0}>
          {data?.files && data?.files?.length > 0 ? (
            <img
              src={`${getFileUrlWithValidToken(data.files[0], sasToken) ?? ''}`}
              height={84}
              width={84}
              style={{ objectFit: 'cover', borderRadius: 8 }}
            />
          ) : (
            <Box
              height={84}
              width={84}
              display="flex"
              alignItems="center"
              justifyContent="center"
              sx={{
                borderRadius: 1,
                backgroundColor: colors.grey[100],
                color: colors.grey[500],
              }}
            >
              <DirectionsCarOutlinedIcon sx={{ fontSize: 40 }} />
            </Box>
          )}
          {onRemove && (
            <IconButton
              size="small"
              onClick={onRemove}
              sx={{
                color: colors.red[500],
                backgroundColor: colors.red[50],
                '&:hover': {
                  backgroundColor: colors.red[100],
                },
              }}
            >
              <IcDeleteTrash />
            </IconButton>
          )}
        </Stack>
      </Stack>
      <Divider sx={{ marginY: 2 }} />
      <Stack flex={1} justifyContent="space-between">
        <Stack gap={1} height={312}>
          {personDetailsItems.map((item, index) => (
            <DetailsItem
              key={index}
              label={item.label}
              value={item.value as string}
            />
          ))}
        </Stack>
        <Stack>
          <Divider sx={{ marginY: 2 }} />
          <RelationsItems data={data} setSelectedTab={setSelectedTab} />
        </Stack>
      </Stack>
    </Stack>
  );
};

export default CarDetailsCard;
